import { Box, Checkbox, Grid, ListItemText, MenuItem, Stack, Typography } from "@mui/material";
import { Control, Controller, FieldErrors, useForm } from "react-hook-form";
import { FormInputField, FormSelectField } from "@ui/form";
import PrimaryButton from "@components/buttons/PrimaryButton";
import palette from "@utils/palette";
import { useAuth } from "@contexts/AuthContext";
import { useState } from "react";
import { Select } from "@ui/select";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

interface ITenantIssue {
    control: Control<any>;
    errors: FieldErrors<any>;
    onNext: () => void;
    setValue: any;
    isValid: boolean;
    units?: any;
}

export default function BulkWork({ setValue, control, errors, onNext, isValid, units }: ITenantIssue) {
    const {user} = useAuth();

    const [selectedCategory, setSelectedCategory] = useState(null as any);

    const [selectedLocations, setSelectedLocations] = useState([] as any[]);

    const [selectedLocationType, setSelectedLocationType] = useState('');

    const onCategorySelect = (e:any) => {
        setValue('category', e.target.value);
        setSelectedCategory(e.target.value);
    }

    const onUnitSelect = (e:any) => {
        console.log(e.target.value);
        const locations = units.filter((unit: any) => unit.locationType === selectedLocationType);
        if(e.target.value.includes('selectall')) {
            setValue('unit', locations.map((u:any) => u.id));
            setSelectedLocations(locations.map((u:any) => u.id));
        } else if(e.target.value.includes('clear')) {
            setValue('unit', []);
            setSelectedLocations([]);
        } else {
            setValue('unit', e.target.value);
            setSelectedLocations(e.target.value);
        }
    }
    return <>
        <Stack sx={{ mt: 3, mb: 12, backgroundColor: palette.white, px: 3, py: 20 / 8 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 3, color: '#5A6A72' }}>Preventive Work</Typography>
            <Grid container rowSpacing={3}>
                <Grid item xs={12}>
                    <Controller
                        name="category"
                        control={control}
                        rules={{
                            required: "Category is required.",
                        }}
                        render={({ field }) => {
                            return (
                                <FormSelectField
                                    label="Category"
                                    {...field}
                                    error={errors?.category}
                                    helperText={errors?.category?.message}
                                    onChange={onCategorySelect}
                                >
                                    {user?.categories?.filter((c) => c.name !== 'Make Ready').map(cat => <MenuItem value={cat.id}>{cat.name}</MenuItem>)}
                                </FormSelectField>
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="issueType"
                        control={control}
                        rules={{
                            required: "Issue Type is required.",
                        }}
                        render={({ field }) => {
                            return (
                                <FormSelectField
                                    label="Issue Type"
                                    {...field}
                                    error={errors?.issueType}
                                    helperText={errors?.issueType?.message}
                                >
                                    {user?.categories?.find(c => c.id === selectedCategory)?.subCategories.map((cat: any) => <MenuItem value={cat.id}>{cat.name}</MenuItem>)}
                                </FormSelectField>
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Stack spacing={1}>
                        <label htmlFor={'locationType_id'}>
                            <Typography variant="body1" sx={{ color: '#303436bf' }}>
                                {'Location Type'}
                            </Typography>
                        </label>
                        <Select
                            IconComponent={ExpandMoreRoundedIcon}
                            fullWidth
                            id={'locationType_id'}
                            sx={{
                                backgroundColor: "white",
                                borderRadius: ".5rem",
                                "& .MuiSelect-select": {
                                    py: 1,
                                },
                                "& svg": {
                                    color: '#30343659'
                                }
                            }}
                            value={selectedLocationType}
                            onChange={(e: any) => {
                                setSelectedLocationType(e.target.value);
                                setValue('unit', []);
                                setSelectedLocations([]);
                            }}
                        >
                            <MenuItem value="Unit">Unit</MenuItem>
                            <MenuItem value="Building">Building</MenuItem>
                        </Select>
                    </Stack>
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="unit"
                        control={control}
                        rules={{
                            required: "Location is required.",
                        }}
                        render={({ field }) => {
                            return (
                                <FormSelectField
                                    label="Location"
                                    {...field}
                                    error={errors?.unit}
                                    helperText={errors?.unit?.message}
                                    multiple
                                    onChange={onUnitSelect}
                                    renderValue={((value: any) => {
                                        return <Typography
                                            sx={{
                                                fontWeight: 400,
                                                color: '#5A6A72',
                                                maxWidth: '500px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            >
                                            {value.length > 0 ? value.map((v: any) => `${selectedLocationType} ${units.find((u: any) => u.id === v)?.name}`).join(', ') : ''}
                                        </Typography>
                                    })}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1 }}>
                                        <MenuItem value="selectall" onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            onUnitSelect({ target: { value: 'selectall'}});
                                        }}>Select All</MenuItem>
                                        <MenuItem value="clear" onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            onUnitSelect({ target: { value: 'clear'}});
                                        }}>Clear</MenuItem>
                                    </Box>
                                    {units && units.filter((unit: any) => unit.locationType === selectedLocationType).map((unit: any) =>
                                        <MenuItem value={unit.id}>
                                            <Checkbox checked={selectedLocations?.includes(unit.id)} />
                                            <ListItemText>{unit.locationType} {unit.name}</ListItemText>
                                        </MenuItem>
                                    )}
                                </FormSelectField>
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="priority"
                        control={control}
                        rules={{
                            required: "Priority is required.",
                        }}
                        render={({ field }) => {
                            return (
                                <FormSelectField
                                    label="Priority"
                                    {...field}
                                    error={errors?.priority}
                                    helperText={errors?.priority?.message}
                                >
                                    <MenuItem value={"High"}>High</MenuItem>
                                    <MenuItem value={"Medium"}>Medium</MenuItem>
                                    <MenuItem value={"Low"}>Low</MenuItem>
                                    <MenuItem value={"No Priority"}>No Priority</MenuItem>
                                </FormSelectField>
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="description"
                        control={control}
                        rules={{
                            required: "Description is required.",
                        }}
                        render={({ field }) => {
                            return (
                                <FormInputField
                                    multiline
                                    minRows={4}
                                    maxRows={4}
                                    label="Description"
                                    {...field}
                                    error={errors?.description}
                                    helperText={errors?.description?.message}
                                />
                            );
                        }}
                    />
                </Grid>
            </Grid>
        </Stack>
        <Stack sx={{ backgroundColor: palette.white, px: 3, py: 20 / 8, marginTop: 'auto', position: 'fixed', bottom: 0, width: '100%', maxWidth: '432px' }}>
            <Box sx={{  }}>
                <PrimaryButton isDisabled={!isValid} isFullWidth={true} onClick={onNext} btnText="Continue" />
            </Box>
        </Stack>
    </>
}